import { StyleObject } from 'styletron-react'

// Shared constants for the Live Updates device-preview mocks.
//
// The values below intentionally sit OUTSIDE constants/palette: this component
// family renders OS device chrome (an iOS lock screen, an Android lock screen,
// an in-app sheet), not Thanx product chrome, so the colors are literal
// platform/brand values rather than theme tokens. Each is annotated per the
// phone-mock exception in CLAUDE.md.
export const previewColors = {
  // Dark-teal iOS lock-screen wallpaper behind the clock and cards.
  iosWallpaper: '#114A51',
  // Lighter teal Android lock-screen wallpaper (lighter treatment per Figma).
  androidWallpaper: '#1C6E75',
  // Dark device bezel framing the top of the phone.
  deviceBezel: '#232B33',
  // Light in-app phone bezel (In-App tab shows a light device).
  lightBezel: '#111417',
  // Translucent dark "glass" surface + hairline matching Apple's Live Activity.
  glassBackground: 'rgba(13, 13, 13, 0.4)',
  glassBorder: 'rgba(255, 255, 255, 0.2)',
  // iOS push-notification banner: translucent dark card on the wallpaper.
  iosPushBackground: 'rgba(28, 28, 30, 0.55)',
  iosPushBorder: 'rgba(255, 255, 255, 0.18)',
  // Thanx brand red used as the app-icon placeholder square + active in-app row.
  brandRed: '#C02227',
  // Android Material notification chrome (light scheme) per the Figma Android
  // lock-screen notifications (430:6257): a green-tinted surface-container with
  // Material on-surface / on-surface-variant text.
  androidSurface: '#E9EFEC',
  // Higher-emphasis Material container, used for the collapsed notification's
  // expand-indicator pill.
  androidSurfaceHigh: '#DDE4E1',
  androidOnSurface: '#171D1B',
  androidOnSurfaceVariant: '#3F4946',
}

// The mocks mimic native OS chrome, so they use the platform system font stack
// rather than the merchant theme's typeface.
export const SYSTEM_FONT_STACK =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, sans-serif'

// Android Material chrome leads with Roboto (the mocks mimic the OS, not the
// merchant theme's typeface).
export const ANDROID_FONT_STACK = 'Roboto, "Segoe UI", system-ui, sans-serif'

// Rendered device width; the frame clamps to 100% of its column on narrow
// viewports so the preview column never triggers horizontal page scroll.
export const DEVICE_WIDTH = '360px'

// A merchant's status description gets a couple of lines on the Live Activity
// cards before clipping (the title is always single-line chrome, matching the
// real OS surfaces, which clip rather than grow).
export const DESCRIPTION_LINE_CLAMP = 2

// Single-line ellipsis truncation for a merchant-edited title.
export const singleLineClamp: StyleObject = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

// Multi-line ellipsis truncation (WebKit box clamp) for a merchant-edited
// description, capped at `lines` rows before clipping.
export const multiLineClamp = (lines: number): StyleObject => ({
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
})

// Android Material type scale for the notification cards (Figma 430:6257).
// Centralized alongside the Material colors so the two Android cards
// (AndroidLiveActivityCard, AndroidPushCard) can't drift apart. Spread into a
// `css({...})` call; layout-specific props (truncation, etc.) are added at the
// call site.
export const androidTitle: StyleObject = {
  fontFamily: ANDROID_FONT_STACK,
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '24px',
  letterSpacing: '0.15px',
  color: previewColors.androidOnSurface,
}
export const androidBody: StyleObject = {
  fontFamily: ANDROID_FONT_STACK,
  fontSize: '14px',
  lineHeight: '20px',
  letterSpacing: '0.25px',
  color: previewColors.androidOnSurfaceVariant,
}
export const androidCaption: StyleObject = {
  fontFamily: ANDROID_FONT_STACK,
  fontSize: '12px',
  lineHeight: '16px',
  letterSpacing: '0.4px',
  color: previewColors.androidOnSurfaceVariant,
}
